"use client";

import { useEffect, useState } from "react";
import { ExternalLink, Star } from "lucide-react";
import { Link } from "react-router-dom";

export default function HiringModal({ show, setShow }) {
  const [isVisible, setIsVisible] = useState(false);
  const [showFlash, setShowFlash] = useState(false);
  const [showBlink, setShowBlink] = useState(false);

  useEffect(() => {
    const flashTimer = setTimeout(() => {
      setShowFlash(true);
      setTimeout(() => setShowFlash(false), 200);
    }, 800);

    const modalTimer = setTimeout(() => {
      
      setShow(true);
      setIsVisible(true);
      setShowBlink(true);
    }, 100);

    return () => {
      clearTimeout(flashTimer);
      clearTimeout(modalTimer);
    };
  }, [setShow]);

  if (!show) return null;

  return (
    <>
      {/* Flash overlay */}
      {showFlash && (
        <div className="fixed inset-0 bg-blue-400/30 z-60 animate-ping pointer-events-none" />
      )}

      {/* Backdrop */}
      <div
        className="fixed inset-0 z-35 animate-in fade-in duration-500 pointer-events-auto"
        style={{
          background:
            "radial-gradient(circle at 85% 15%, rgba(59,130,246,0.1) 0%, rgba(0,0,0,0.2) 100%)",
        }}
      />

      {/* Modal container - centered horizontally */}
      <div  className="fixed top-20 left-1/2 -translate-x-1/2 z-50 w-auto px-2 md:px-4  py-2">
        <div
          className={`relative bg-gradient-to-br from-slate-100 via-blue-50 to-purple-50
            shadow-2xl rounded-xl overflow-hidden backdrop-blur-sm
            transform transition-all duration-700 ease-out
            ${isVisible ? "translate-y-0 opacity-100 scale-100" : "translate-y-8 opacity-0 scale-95"}
            ${showBlink ? "animate-blink-border" : ""}`}
          style={{
            boxShadow: showBlink
              ? `0 0 60px rgba(59,130,246,0.6), 0 0 100px rgba(139,92,246,0.4)`
              : `0 0 40px rgba(99,102,241,0.1)`,
          }}
        >
          {/* Clickable overlay */}
          <Link to="/careers" className="absolute inset-0 z-10">
            <span className="sr-only">Go to careers</span>
          </Link>

          {/* Background shimmer */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-400/10 via-purple-400/10 to-indigo-400/10 rounded-xl animate-flash-bg" />

          {/* Header */}
          <Link to="/careers">
          
          <div className="relative p-3 bg-gradient-to-r from-blue-500/90 to-purple-600/90 text-white animate-header-blink z-20">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Star className="h-6 w-6 text-yellow-300 animate-blink-fast" fill="currentColor" />
                  <div className="absolute inset-0 animate-ping">
                    <Star className="h-6 w-6 text-yellow-200/70" fill="currentColor" />
                  </div>
                  <div className="absolute inset-0 animate-pulse">
                    <Star className="h-6 w-6 text-white/50" fill="currentColor" />
                  </div>
                  <div className="absolute inset-0 animate-spin-slow">
                    <Star className="h-6 w-6 text-yellow-100/50" fill="currentColor" />
                  </div>
                </div>
                <h2 className="font-semibold text-[18px]  animate-text-blink whitespace-nowrap">
                  We're Hiring!
                </h2>
              </div>

              <span
               
                className="z-30 pl-3 text-white/80 hover:text-white hover:bg-white/20 rounded-full p-1 transition-all duration-200"
              >
                <ExternalLink className="w-5 h-5 transition-transform duration-200" />
              </span>
            </div>
          </div>
          </Link>

          {/* Glow dots for shimmer effect */}
          <div className="absolute -top-3 -right-3 flex gap-1 z-20">
            <div className="w-3 h-3 md:w-4 md:h-4 bg-gradient-to-r from-red-500 to-pink-500 rounded-full animate-ping" />
            <div className="absolute top-0 right-0 w-3 h-3 md:w-4 md:h-4 bg-gradient-to-r from-yellow-500 to-red-500 rounded-full animate-blink-fast" />
          </div>
          <div className="absolute -bottom-3 -left-3 z-20">
            <div className="w-4 h-4 md:w-6 md:h-6 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full animate-pulse" />
          </div>

          {/* Extra shimmer stars */}
          <div className="absolute -top-4 left-2 w-2 h-2 bg-yellow-400 rounded-full animate-blink-fast" />
          <div className="absolute top-2 right-4 w-2 h-2 bg-blue-400 rounded-full animate-ping" />
          <div className="absolute bottom-2 left-3 w-2 h-2 bg-purple-500 rounded-full animate-blink-fast" />
        </div>
      </div>

      {/* Animations */}
      <style jsx>{`
        @keyframes blink-border {
          0%, 100% {
            border-color: #3b82f6;
            box-shadow: 0 0 20px rgba(59,130,246,0.8), 0 0 30px rgba(139,92,246,0.4);
          }
          25% {
            border-color: #f59e0b;
            box-shadow: 0 0 25px rgba(245,158,11,0.6), 0 0 40px rgba(236,72,153,0.3);
          }
          50% {
            border-color: #8b5cf6;
            box-shadow: 0 0 30px rgba(139,92,246,0.7), 0 0 50px rgba(59,130,246,0.5);
          }
          75% {
            border-color: #ec4899;
            box-shadow: 0 0 30px rgba(236,72,153,0.6), 0 0 50px rgba(147,51,234,0.4);
          }
        }

        @keyframes flash-bg {
          0%, 100% { opacity: 0.05; }
          50% { opacity: 0.15; }
        }

        @keyframes header-blink {
          0%, 100% {
            background: linear-gradient(to right, rgba(59,130,246,0.9), rgba(139,92,246,0.9));
          }
          33% {
            background: linear-gradient(to right, rgba(239,68,68,0.9), rgba(59,130,246,0.9));
          }
          66% {
            background: linear-gradient(to right, rgba(139,92,246,0.9), rgba(236,72,153,0.9));
          }
        }

        @keyframes blink-fast {
          0%, 50% { opacity: 1; transform: scale(1); }
          25% { opacity: 0.3; transform: scale(1.2); }
          75% { opacity: 0.7; transform: scale(0.9); }
        }

        @keyframes text-blink {
          0%, 100% { opacity: 1; color: white; }
          50% { opacity: 0.7; color: #fbbf24; }
        }

        @keyframes spin-slow {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .animate-blink-border { animation: blink-border 2.5s infinite; }
        .animate-flash-bg { animation: flash-bg 3s infinite; }
        .animate-header-blink { animation: header-blink 4s infinite; }
        .animate-blink-fast { animation: blink-fast 1s infinite; }
        .animate-text-blink { animation: text-blink 2s infinite; }
        .animate-spin-slow { animation: spin-slow 6s linear infinite; }
      `}</style>
    </>
  );
}

